import React from "react";
import { Link } from "@tanstack/react-router";
import UserProfile from "../../../assets/ProfileIcon/IconButton_User.svg";
import XIcon from "../../../assets/Icon/X_Blue.svg";
import UserIcon from "../../../assets/Icon/User_Blue.svg";
import HomeIcon from "../../../assets/Icon/Home_Blue.svg";
import CheckCircleIcon from "../../../assets/Icon/CheckCircle_Blue.svg";
import BellIcon from "../../../assets/Icon/Bell_Blue.svg";
import ClipboardIcon from "../../../assets/Icon/Clipboard_Blue.svg";
import LockIcon from "../../../assets/Icon/Lock_Blue.svg";
import ChevronRightIcon from "../../../assets/Chevron/ChevronRight_Blue.svg";
import { useOwnPatientDetail } from "../../../contexts/OwnPatientDetailContext";
import { initAmplify } from "../../../lib/amplify";
import { getLink } from "../../../constants/links";
import {
  signOutPatientWithMediaCookieRevoke,
  signOutWithMediaCookieRevoke,
} from "../../../lib/logout";
import { isPatientSelfAuthEnabled } from "../../../lib/featureFlags";

type MenuOverlayProps = {
  isOpen: boolean;
  onClose: () => void;
};

const menuItems = [
  { icon: HomeIcon, label: "ホーム", path: "/patient/home" },
  { icon: UserIcon, label: "お客様の基本情報設定", path: "/patient/profile" },
  // 診療履歴は診断レポート一覧へ誘導
  {
    icon: UserIcon,
    label: "お客様の診療履歴",
    path: "/patient/diagnostic-report/list",
  },
  // 請求履歴の導線を追加
  { icon: ClipboardIcon, label: "請求履歴", path: "/patient/invoice-list" },
  { icon: CheckCircleIcon, label: "やること", path: "/patient/task-list" },
  { icon: BellIcon, label: "おしらせ", path: "/patient/notification-list" },
  {
    icon: ClipboardIcon,
    label: "問診票",
    path: "/patient/questionnaire/",
  },
  { icon: LockIcon, label: "パスワード変更", path: "/patient/password-change" },
];

const legalMenuItems = [
  { label: "利用規約", path: getLink("TERMS_OF_USE_PDF_FOR_PATIENT") },
  { label: "プライバシーポリシー", path: getLink("PRIVACY_POLICY") },
  { label: "特定商取引の表示", path: getLink("COMMERCIAL_TRANSCTION_ACT") },
  { label: "運営会社", path: getLink("OPERATING_COMPANY") },
];

export function MenuOverlay({ isOpen, onClose }: MenuOverlayProps) {
  const { patientName, companyName } = useOwnPatientDetail();

  const onLogout = async () => {
    if (isPatientSelfAuthEnabled()) {
      try {
        await signOutPatientWithMediaCookieRevoke();
      } catch {
        // noop
      } finally {
        onClose();
      }
      return;
    }
    try {
      // Ensure Amplify is configured before using Auth APIs
      initAmplify();
    } catch {
      // Amplify may already be initialized; ignore
    }
    try {
      await signOutWithMediaCookieRevoke();
    } catch {
      // noop
    } finally {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-brandBlue z-50 flex flex-col items-center overflow-y-auto"
      data-name="メニュー"
      data-node-id="1191:18652"
    >
      <div className="w-full max-w-[440px]">
        {/* Header */}
        <div
          className="bg-brandBlue flex h-[70px] items-center justify-end px-[22px] py-[11px] w-full"
          data-node-id="1191:18686"
        >
          <button
            type="button"
            onClick={onClose}
            className="p-2"
            data-name="Icon Button"
            data-node-id="1191:25452"
          >
            <img src={XIcon} alt="閉じる" className="size-[20px]" />
          </button>
        </div>

        {/* User Info and Logout */}
        <div
          className="flex gap-[12px] items-center px-[10px] py-0 w-full"
          data-node-id="1191:25451"
        >
          <div
            className="relative size-[50px]"
            data-name="Icon Button"
            data-node-id="1211:4495"
          >
            <div className="absolute inset-0 bg-white rounded-full" />
            <img
              alt="User"
              className="absolute left-[-14%] top-[-14%] size-[128%] max-w-none"
              src={UserProfile}
            />
          </div>
          <div
            className="flex-1 flex flex-col justify-center h-[50px]"
            data-node-id="1211:4490"
          >
            <p
              className="font-bold text-2xl text-white"
              data-node-id="1211:4491"
            >
              {patientName ?? "氏名未設定"}
            </p>
            <p className="text-sm text-white" data-node-id="1211:4493">
              {companyName ?? "企業名未設定"}
            </p>
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="border border-[var(--nttクリームブルー,#bad2ed)] rounded-full px-4 py-3"
            data-name="Menu Item"
            data-node-id="1191:25443"
          >
            <p
              className="font-bold text-base text-white"
              data-node-id="1191:25446"
            >
              ログアウト
            </p>
          </button>
        </div>

        {/* Menu List */}
        <div className="flex flex-col mt-[30px]" data-node-id="1191:18655">
          {menuItems.map((item) => (
            <Link
              to={item.path}
              key={item.label}
              className="flex items-center p-5 w-full"
              onClick={onClose}
            >
              <img src={item.icon} alt="" className="size-[30px] mr-5" />
              <p className="text-lg text-white">{item.label}</p>
            </Link>
          ))}
        </div>

        {/* Legal Links */}
        <div
          className="flex flex-wrap gap-x-[10px] gap-y-2.5 p-5 mt-[10px]"
          data-node-id="1191:25301"
        >
          {legalMenuItems.map((item) => {
            const isExternal =
              item.path.startsWith("http") || item.path.endsWith(".pdf");
            return isExternal ? (
              <a
                href={item.path}
                key={item.label}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center"
                onClick={onClose}
              >
                <img src={ChevronRightIcon} alt="" className="size-5 mr-1" />
                <p className="text-base text-white">{item.label}</p>
              </a>
            ) : (
              <Link
                to={item.path}
                key={item.label}
                className="flex items-center"
                onClick={onClose}
              >
                <img src={ChevronRightIcon} alt="" className="size-5 mr-1" />
                <p className="text-base text-white">{item.label}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
